import { supabase } from '../lib/supabase';

export const clientSelfServiceService = {
  /**
   * Request password reset email
   */
  async requestPasswordReset(email) {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/client-self-service-account-recovery-portal?tab=password-reset&action=confirm`
      });

      if (error) throw error;

      return { success: true, message: 'Password reset email sent successfully' };
    } catch (error) {
      console.error('Password reset request error:', error);
      return { success: false, message: error?.message || 'Failed to send password reset email' };
    }
  },

  /**
   * Update password after reset
   */
  async updatePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;

      return { success: true, message: 'Password updated successfully' };
    } catch (error) {
      console.error('Password update error:', error);
      return { success: false, message: error?.message || 'Failed to update password' };
    }
  },

  /**
   * Verify account exists by email
   */
  async verifyAccountExists(email) {
    try {
      const { data, error } = await supabase
        ?.from('company_settings')
        ?.select('id, company_name, contact_email, is_active')
        ?.eq('contact_email', email)
        ?.maybeSingle();

      if (error) throw error;

      return {
        exists: !!data,
        companyId: data?.id,
        companyName: data?.company_name,
        isActive: data?.is_active
      };
    } catch (error) {
      console.error('Account verification error:', error);
      return { exists: false };
    }
  },

  /**
   * Send account recovery email
   */
  async sendAccountRecoveryEmail(email, verificationType) {
    try {
      // First verify account exists
      const accountCheck = await this.verifyAccountExists(email);
      
      if (!accountCheck?.exists) {
        return { success: false, message: 'No account found with this email address' };
      }

      // Send password reset email as recovery method
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/client-self-service-account-recovery-portal?tab=account-recovery&action=confirm`
      });

      if (error) throw error;

      return { 
        success: true, 
        message: 'Account recovery email sent successfully',
        companyName: accountCheck?.companyName
      };
    } catch (error) {
      console.error('Account recovery error:', error);
      return { success: false, message: error?.message || 'Failed to send recovery email' };
    }
  },

  /**
   * Verify payment by transaction reference
   */
  async verifyPaymentTransaction(verificationData) {
    try {
      const { transactionReference, amount, bankName, companyEmail } = verificationData;

      // First verify company exists
      const { data: company, error: companyError } = await supabase
        ?.from('company_settings')
        ?.select('id, company_name')
        ?.eq('contact_email', companyEmail)
        ?.maybeSingle();

      if (companyError || !company) {
        return { 
          success: false, 
          message: 'Company account not found. Please check your email address.' 
        };
      }

      // Search for matching payment transaction
      const { data: payments, error: paymentError } = await supabase
        ?.from('payment_transactions')
        ?.select('*')
        ?.eq('company_id', company?.id)
        ?.eq('payment_method', bankName?.toLowerCase())
        ?.eq('reconciliation_status', 'pending')
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (paymentError) throw paymentError;

      // Check for exact match
      const exactMatch = payments?.find(p => 
        p?.transaction_reference === transactionReference &&
        Math.abs(parseFloat(p?.amount) - parseFloat(amount)) < 0.01
      );

      if (exactMatch) {
        return {
          success: true,
          status: 'matched',
          message: 'Payment found and pending verification by admin',
          paymentDetails: {
            amount: exactMatch?.amount,
            currency: exactMatch?.currency,
            transactionDate: exactMatch?.created_at,
            status: exactMatch?.reconciliation_status
          }
        };
      }

      // Check for partial match (amount only)
      const amountMatch = payments?.find(p => 
        Math.abs(parseFloat(p?.amount) - parseFloat(amount)) < 0.01
      );

      if (amountMatch) {
        return {
          success: true,
          status: 'partial_match',
          message: 'Payment amount matched but transaction reference differs. Please contact support.',
          paymentDetails: {
            amount: amountMatch?.amount,
            currency: amountMatch?.currency,
            transactionDate: amountMatch?.created_at,
            status: amountMatch?.reconciliation_status
          }
        };
      }

      return {
        success: false,
        status: 'not_found',
        message: 'No matching payment found. Please ensure transaction details are correct or allow 24-48 hours for bank processing.'
      };
    } catch (error) {
      console.error('Payment verification error:', error);
      return { 
        success: false, 
        message: 'Failed to verify payment. Please try again later.' 
      };
    }
  },

  /**
   * Get payment history for company
   */
  async getCompanyPaymentHistory(companyEmail) {
    try {
      const { data: company, error: companyError } = await supabase
        ?.from('company_settings')
        ?.select('id')
        ?.eq('contact_email', companyEmail)
        ?.maybeSingle();

      if (companyError || !company) {
        return [];
      }

      const { data, error } = await supabase
        ?.from('payment_transactions')
        ?.select('*')
        ?.eq('company_id', company?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(5);

      if (error) throw error;

      return data?.map(payment => ({
        id: payment?.id,
        amount: payment?.amount,
        currency: payment?.currency,
        paymentMethod: payment?.payment_method,
        transactionReference: payment?.transaction_reference,
        status: payment?.reconciliation_status,
        createdAt: payment?.created_at
      })) || [];
    } catch (error) {
      console.error('Payment history error:', error);
      return [];
    }
  },

  /**
   * Validate password strength
   */
  validatePasswordStrength(password) {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/?.test(password);
    const hasLowerCase = /[a-z]/?.test(password);
    const hasNumbers = /\d/?.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/?.test(password);

    const errors = [];
    if (password?.length < minLength) errors?.push(`At least ${minLength} characters`);
    if (!hasUpperCase) errors?.push('One uppercase letter');
    if (!hasLowerCase) errors?.push('One lowercase letter');
    if (!hasNumbers) errors?.push('One number');
    if (!hasSpecialChar) errors?.push('One special character');

    return {
      isValid: errors?.length === 0,
      errors,
      strength: this.calculatePasswordStrength(password)
    };
  },

  calculatePasswordStrength(password) {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (password?.length >= 12) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/?.test(password)) strength++;

    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  }
};

export default clientSelfServiceService;